export const DEFAULT_BETA_SOURCE = 'yahoo';
export const FACTOR_BETA_SOURCE = 'factor';
export const WATCHLIST_BETA_SOURCE_PREFIX = 'watchlist:';
export const WATCHLIST_UNNAMED_BETA_SOURCE = `${WATCHLIST_BETA_SOURCE_PREFIX}unnamed`;

export function normalizeBetaSource(value) {
    const raw = value == null ? '' : String(value).trim();
    const normalized = raw.toLowerCase();
    if (normalized === FACTOR_BETA_SOURCE || normalized === DEFAULT_BETA_SOURCE) {
        return normalized;
    }
    if (normalized.startsWith(WATCHLIST_BETA_SOURCE_PREFIX)) {
        return normalized;
    }
    return DEFAULT_BETA_SOURCE;
}

function fallbackLabelForBetaSource(sourceId) {
    const normalized = normalizeBetaSource(sourceId);
    if (normalized === FACTOR_BETA_SOURCE) return 'Factor';
    if (normalized === DEFAULT_BETA_SOURCE) return 'Yahoo';
    if (normalized === WATCHLIST_UNNAMED_BETA_SOURCE) return '<unnamed> (via watchlist)';
    if (normalized.startsWith(WATCHLIST_BETA_SOURCE_PREFIX)) {
        return `${normalized.slice(WATCHLIST_BETA_SOURCE_PREFIX.length)} (via watchlist)`;
    }
    return 'Yahoo';
}

function normalizeAvailabilityEntry(sourceId, rawEntry) {
    const normalizedSourceId = normalizeBetaSource(sourceId);
    if (!rawEntry || typeof rawEntry !== 'object' || Array.isArray(rawEntry)) {
        return null;
    }
    return {
        available: rawEntry.available !== false,
        label: rawEntry.label || fallbackLabelForBetaSource(normalizedSourceId),
        kind: normalizedSourceId.startsWith(WATCHLIST_BETA_SOURCE_PREFIX) ? 'watchlist' : 'static',
        sourceName: rawEntry.source_name ?? rawEntry.sourceName ?? null,
        columnName: rawEntry.column_name ?? rawEntry.columnName ?? null,
    };
}

export function getAvailableBetaSources(userData = null) {
    const rawAvailability = userData?.available_beta_sources || {};
    const availability = {
        [DEFAULT_BETA_SOURCE]: {
            available: true,
            label: 'Yahoo',
            kind: 'static',
            sourceName: null,
            columnName: null,
        },
        [FACTOR_BETA_SOURCE]: {
            available: true,
            label: 'Factor',
            kind: 'static',
            sourceName: null,
            columnName: null,
        },
    };

    Object.entries(rawAvailability).forEach(([sourceId, rawEntry]) => {
        const normalizedEntry = normalizeAvailabilityEntry(sourceId, rawEntry);
        if (!normalizedEntry) return;
        availability[normalizeBetaSource(sourceId)] = normalizedEntry;
    });

    return availability;
}

export function getBetaAvailabilityKey(rawAvailability = null) {
    const availability = rawAvailability
        && typeof rawAvailability === 'object'
        && !Array.isArray(rawAvailability)
        ? rawAvailability
        : {};
    return JSON.stringify(
        Object.entries(getAvailableBetaSources({ available_beta_sources: availability }))
            .map(([source, entry]) => [
                source,
                entry.available !== false,
                entry.label || '',
                entry.sourceName || '',
                entry.columnName || '',
            ])
            .sort(([left], [right]) => left.localeCompare(right)),
    );
}

export function getAvailableBetaSourceOptions(userData = null) {
    return Object.entries(getAvailableBetaSources(userData))
        .filter(([, entry]) => entry.available !== false)
        .map(([value, entry]) => ({
            value,
            label: entry.label || fallbackLabelForBetaSource(value),
            kind: entry.kind,
            sourceName: entry.sourceName,
            columnName: entry.columnName,
        }));
}

export function isBetaSourceAvailable(availability, sourceId) {
    const normalizedSourceId = normalizeBetaSource(sourceId);
    const entry = availability?.[normalizedSourceId];
    return Boolean(entry && entry.available !== false);
}

export function formatBetaSourceLabel(sourceId, availability = null) {
    const normalizedSourceId = normalizeBetaSource(sourceId);
    const entry = availability?.[normalizedSourceId];
    if (entry && typeof entry === 'object' && entry.label) {
        return entry.label;
    }
    return fallbackLabelForBetaSource(normalizedSourceId);
}
